import { useSearchParams } from 'react-router';
import { useCallback } from 'react';

/**
 * Custom hook for managing DataGrid pagination and sorting via URL search params
 *
 * @param {Object} defaultParams - Default pagination/sorting values
 * @returns {Object} { parameters, handlePaginationModelChange, handleSortModelChange }
 */
export const useDataGridState = (defaultParams = {}) => {
	const [searchParams, setSearchParams] = useSearchParams();

	// Extract current parameters from URL or use defaults
	const parameters = {
		page: Number(searchParams.get('page')) || defaultParams.page || 0,
		per: Number(searchParams.get('per')) || defaultParams.per || 10,
		sort_column:
			searchParams.get('sort_column') ||
			defaultParams.sort_column ||
			'created_at',
		sort_order:
			searchParams.get('sort_order') || defaultParams.sort_order || 'desc',
	};

	// Handle MUI DataGrid pagination events
	const handlePaginationModelChange = useCallback(
		({ page, pageSize }) => {
			const newParams = Object.fromEntries(searchParams);
			newParams.page = page;
			newParams.per = pageSize;
			setSearchParams(newParams, { replace: true });
		},
		[searchParams, setSearchParams]
	);

	// Handle MUI DataGrid sorting events
	const handleSortModelChange = useCallback(
		(newSortModel) => {
			const newParams = Object.fromEntries(searchParams);

			if (newSortModel.length === 0) {
				delete newParams.sort_column;
				delete newParams.sort_order;
			} else {
				const { field, sort } = newSortModel[0];
				newParams.sort_column = field;
				newParams.sort_order = sort;
			}

			setSearchParams(newParams, { replace: true });
		},
		[searchParams, setSearchParams]
	);

	return {
		parameters,
		handlePaginationModelChange,
		handleSortModelChange,
	};
};
